import React, { useMemo } from "react";
import { Text } from "@react-three/drei";

const PADDING_RATIO = 0.2;
const CHAR_ASPECT_RATIO = 0.6;

const BinLabel = ({ position, size, binCode, rackInfo }) => {
  const [w, binHeight, d] = size;

  // Determine orientation based on rack footprint
  const isRackHorizontal = useMemo(() => {
    return rackInfo.x2 - rackInfo.x1 > rackInfo.y2 - rackInfo.y1;
  }, [rackInfo]);

  const FONT_REDUCTION = 0.55; // 75% of current size, adjust if needed

  // Memoized font size calculation
  const fontSize = useMemo(() => {
    const availableWidth = w * (1 - PADDING_RATIO);
    const availableDepth = d * (1 - PADDING_RATIO);
    const maxLabelHeight = binHeight * (1 - PADDING_RATIO);
    const maxLabelWidth = isRackHorizontal ? availableWidth : availableDepth;
    const maxFontSizeWidth = maxLabelWidth / (binCode.length * CHAR_ASPECT_RATIO);
    return Math.min(maxLabelHeight, maxFontSizeWidth) * FONT_REDUCTION;
  }, [w, d, binHeight, isRackHorizontal, binCode.length]);

  // Generate both label positions in one memoized calculation
  const labelData = useMemo(() => {
    const [x, y, z] = position;
    const offset = 0.01;
    if (isRackHorizontal) {
      return [
        { position: [x, y, z + d / 2 + offset], rotation: [0, 0, 0] },
        { position: [x, y, z - d / 2 - offset], rotation: [0, Math.PI, 0] },
      ];
    } else {
      return [
        { position: [x + w / 2 + offset, y, z], rotation: [0, Math.PI / 2, 0] },
        { position: [x - w / 2 - offset, y, z], rotation: [0, -Math.PI / 2, 0] },
      ];
    }
  }, [position, w, d, isRackHorizontal]);

  return labelData.map(({ position, rotation }, idx) => (
    <Text
      key={idx}
      position={position}
      rotation={rotation}
      fontSize={fontSize}
      fontWeight={500}
      color="#333"
      lineHeight={0.8}
      letterSpacing={0.02}
      textAlign="center"
    >
      {binCode}
    </Text>
  ));
};

export default BinLabel;
